import { createTheme, alpha, PaletteMode } from '@mui/material/styles';

// Corporate branding colors
const corporateGreen = '#289546';
const corporateDarkGreen = '#1E7037';
const corporateLightGreen = '#34B65C';

// Dark mode colors (keeping the beautiful purple theme)
const darkPrimary = '#6366F1'; // Modern indigo
const darkSecondary = '#8B5CF6'; // Purple accent

// Classic theme colors from old ITCP app
const classicTopBar = '#2b3e7d';  // InvaroSoft top bar
const classicSidebar = '#33344a';  // InvaroSoft sidebar
const classicPrimary = '#3099c5';  // WordBlue - main blue
const classicDarkBlue = '#0c6991';  // WordDarkBlue
const classicLightBlue = '#45b6e5';  // WordLightBlue
const classicButtonPrimary = '#3c3e5f';  // Primary button color
const classicButtonHover = '#3c4270';  // Primary button hover
const classicIconHighlight = '#3c4260';  // Icon highlight
const classicGreen = '#23a048';  // Theme green

// Shared colors
const successColor = '#10B981';
const warningColor = '#F59E0B';
const errorColor = '#EF4444';
const infoColor = '#3B82F6';

export type ThemePreset = 'modern' | 'classic' | 'custom';

export const createAppTheme = (mode: PaletteMode, preset: ThemePreset = 'modern') => {
  const isLight = mode === 'light';
  
  // Select colors based on preset
  let primaryMain, primaryLight, primaryDark, secondaryMain, secondaryLight, secondaryDark;
  let backgroundDefault, backgroundPaper, fontFamily;
  
  if (preset === 'classic') {
    // Classic InvaroSoft theme (exact colors from old app)
    primaryMain = classicPrimary;
    primaryLight = classicLightBlue;
    primaryDark = classicDarkBlue;
    secondaryMain = classicButtonPrimary;
    secondaryLight = classicIconHighlight;
    secondaryDark = classicSidebar;
    // Classic theme but still respect dark/light mode for backgrounds
    backgroundDefault = isLight ? '#efefef' : '#0F0F1A';  // Light: old app color, Dark: modern dark
    backgroundPaper = isLight ? '#fafafa' : '#1A1A2E';  // Light: old app color, Dark: modern dark
    fontFamily = '"Lato", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, sans-serif';
  } else if (preset === 'custom') {
    // Custom theme - will be configured by user
    // For now, use modern as fallback
    primaryMain = isLight ? corporateGreen : darkPrimary;
    primaryLight = isLight ? corporateLightGreen : '#818CF8';
    primaryDark = isLight ? corporateDarkGreen : '#4F46E5';
    secondaryMain = isLight ? corporateDarkGreen : darkSecondary;
    secondaryLight = isLight ? corporateLightGreen : '#A78BFA';
    secondaryDark = isLight ? '#0D5221' : '#7C3AED';
    backgroundDefault = isLight ? '#FAFAFA' : '#0F0F1A';
    backgroundPaper = isLight ? '#FFFFFF' : '#1A1A2E';
    fontFamily = '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  } else {
    // Modern theme (default)
    primaryMain = isLight ? corporateGreen : darkPrimary;
    primaryLight = isLight ? corporateLightGreen : '#818CF8';
    primaryDark = isLight ? corporateDarkGreen : '#4F46E5';
    secondaryMain = isLight ? corporateDarkGreen : darkSecondary;
    secondaryLight = isLight ? corporateLightGreen : '#A78BFA';
    secondaryDark = isLight ? '#0D5221' : '#7C3AED';
    backgroundDefault = isLight ? '#FAFAFA' : '#0F0F1A';
    backgroundPaper = isLight ? '#FFFFFF' : '#1A1A2E';
    fontFamily = '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  }
  
  return createTheme({
    palette: {
      mode,
      primary: {
        main: primaryMain,
        light: primaryLight,
        dark: primaryDark,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: secondaryMain,
        light: secondaryLight,
        dark: secondaryDark,
        contrastText: '#FFFFFF',
      },
      success: {
        main: successColor,
        light: '#34D399',
        dark: '#059669',
        contrastText: '#FFFFFF',
      },
      warning: {
        main: warningColor,
        light: '#FBBF24',
        dark: '#D97706',
        contrastText: '#FFFFFF',
      },
      error: {
        main: errorColor,
        light: '#F87171',
        dark: '#DC2626',
        contrastText: '#FFFFFF',
      },
      info: {
        main: infoColor,
        light: '#60A5FA',
        dark: '#2563EB',
        contrastText: '#FFFFFF',
      },
      background: {
        default: backgroundDefault,
        paper: backgroundPaper,
      },
      text: {
        primary: isLight ? '#1F2937' : '#F1F5F9',
        secondary: isLight ? '#6B7280' : '#94A3B8',
      },
      divider: isLight ? alpha('#D1D5DB', 0.6) : alpha('#64748B', 0.12),
    },
    typography: {
      fontFamily: fontFamily,
      h1: {
        fontSize: '2.5rem',
        fontWeight: 700,
        letterSpacing: '-0.02em',
        lineHeight: 1.2,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        lineHeight: 1.3,
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        lineHeight: 1.4,
      },
      h4: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: '0.9375rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.6,
      },
      button: {
        textTransform: 'none',
        fontWeight: 500,
        letterSpacing: 0.3,
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            padding: '10px 20px',
            fontSize: '0.9375rem',
            fontWeight: 500,
            boxShadow: 'none',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: isLight 
                ? '0 8px 20px rgba(40, 149, 70, 0.25)' 
                : '0 8px 20px rgba(99, 102, 241, 0.25)',
            },
          },
          contained: {
            background: isLight 
              ? corporateGreen
              : `linear-gradient(135deg, ${darkPrimary} 0%, ${darkSecondary} 100%)`,
            '&:hover': {
              background: isLight 
                ? corporateDarkGreen
                : `linear-gradient(135deg, ${darkPrimary} 0%, ${darkSecondary} 100%)`,
              filter: isLight ? 'none' : 'brightness(1.1)',
            },
          },
          outlined: {
            borderWidth: 2,
            borderColor: isLight ? corporateGreen : darkPrimary,
            color: isLight ? corporateGreen : darkPrimary,
            '&:hover': {
              borderWidth: 2,
              borderColor: isLight ? corporateDarkGreen : darkPrimary,
              background: isLight 
                ? alpha(corporateGreen, 0.08)
                : alpha(darkPrimary, 0.08),
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: isLight ? '#FFFFFF' : '#1A1A2E',
            borderRadius: 16,
            border: isLight 
              ? `1px solid ${alpha('#E5E7EB', 0.8)}`
              : `1px solid ${alpha('#64748B', 0.1)}`,
            backdropFilter: isLight ? 'none' : 'blur(10px)',
            boxShadow: isLight 
              ? '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)'
              : '0 4px 24px rgba(0, 0, 0, 0.3)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            backgroundColor: isLight 
              ? '#FFFFFF'
              : alpha('#1A1A2E', 0.8),
            backdropFilter: isLight ? 'none' : 'blur(20px)',
            border: isLight 
              ? `1px solid ${alpha('#E5E7EB', 0.8)}`
              : `1px solid ${alpha('#64748B', 0.1)}`,
            boxShadow: isLight
              ? '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.06)'
              : '0 8px 32px rgba(0, 0, 0, 0.4)',
            transition: 'box-shadow 0.2s ease, transform 0.2s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: isLight
                ? '0 20px 25px rgba(40, 149, 70, 0.15), 0 10px 10px rgba(0, 0, 0, 0.04)'
                : '0 12px 48px rgba(99, 102, 241, 0.2)',
              borderColor: isLight
                ? alpha(corporateGreen, 0.3)
                : alpha(darkPrimary, 0.3),
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,
              backgroundColor: isLight 
                ? '#FAFAFA'
                : alpha('#000000', 0.3),
              transition: 'all 0.2s',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isLight 
                  ? alpha('#D1D5DB', 0.8)
                  : alpha('#64748B', 0.3),
              },
              '&:hover': {
                backgroundColor: isLight 
                  ? '#F5F5F5'
                  : alpha('#000000', 0.4),
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isLight
                    ? alpha(corporateGreen, 0.5)
                    : alpha(darkPrimary, 0.5),
                },
              },
              '&.Mui-focused': {
                backgroundColor: isLight 
                  ? '#FFFFFF'
                  : alpha('#000000', 0.5),
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isLight ? corporateGreen : darkPrimary,
                  borderWidth: 2,
                },
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
            fontSize: '0.8125rem',
          },
          filled: {
            background: isLight
              ? alpha(corporateGreen, 0.15)
              : alpha(darkPrimary, 0.15),
            color: isLight ? corporateGreen : darkPrimary,
            '&:hover': {
              background: isLight
                ? alpha(corporateGreen, 0.25)
                : alpha(darkPrimary, 0.25),
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: isLight
                ? alpha(corporateGreen, 0.1)
                : alpha(darkPrimary, 0.1),
              transform: 'scale(1.1)',
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.9375rem',
            minHeight: 48,
            borderRadius: 8,
            margin: '0 4px',
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: isLight
                ? alpha(corporateGreen, 0.08)
                : alpha(darkPrimary, 0.08),
            },
            '&.Mui-selected': {
              background: isLight
                ? alpha(corporateGreen, 0.12)
                : `linear-gradient(135deg, ${alpha(darkPrimary, 0.15)} 0%, ${alpha(darkSecondary, 0.15)} 100%)`,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: 3,
            background: isLight
              ? corporateGreen
              : `linear-gradient(90deg, ${darkPrimary} 0%, ${darkSecondary} 100%)`,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            fontSize: '0.9375rem',
          },
          standardSuccess: {
            backgroundColor: alpha(successColor, 0.15),
            color: isLight ? '#059669' : successColor,
            border: `1px solid ${alpha(successColor, 0.3)}`,
          },
          standardError: {
            backgroundColor: alpha(errorColor, 0.15),
            color: isLight ? '#DC2626' : errorColor,
            border: `1px solid ${alpha(errorColor, 0.3)}`,
          },
          standardWarning: {
            backgroundColor: alpha(warningColor, 0.15),
            color: isLight ? '#D97706' : warningColor,
            border: `1px solid ${alpha(warningColor, 0.3)}`,
          },
          standardInfo: {
            backgroundColor: alpha(infoColor, 0.15),
            color: isLight ? '#2563EB' : infoColor,
            border: `1px solid ${alpha(infoColor, 0.3)}`,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isLight 
              ? alpha('#1F2937', 0.95)
              : alpha('#000000', 0.9),
            borderRadius: 8,
            fontSize: '0.8125rem',
            padding: '8px 12px',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isLight ? '#FFFFFF' : '#1A1A2E',
            backgroundImage: 'none',
            boxShadow: isLight
              ? '0 1px 3px rgba(0, 0, 0, 0.05)'
              : '0 4px 24px rgba(0, 0, 0, 0.3)',
            borderBottom: isLight
              ? `1px solid ${alpha('#E5E7EB', 0.8)}`
              : `1px solid ${alpha('#64748B', 0.1)}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isLight ? '#FFFFFF' : '#1A1A2E',
            backgroundImage: 'none',
            borderRight: isLight
              ? `1px solid ${alpha('#E5E7EB', 0.8)}`
              : `1px solid ${alpha('#64748B', 0.1)}`,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: isLight
              ? alpha('#E5E7EB', 0.8)
              : alpha('#64748B', 0.2),
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '2px 8px',
            '&:hover': {
              backgroundColor: isLight
                ? alpha(corporateGreen, 0.08)
                : alpha(darkPrimary, 0.08),
            },
            '&.Mui-selected': {
              backgroundColor: isLight
                ? alpha(corporateGreen, 0.12)
                : alpha(darkPrimary, 0.12),
              '&:hover': {
                backgroundColor: isLight
                  ? alpha(corporateGreen, 0.16)
                  : alpha(darkPrimary, 0.16),
              },
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: isLight ? corporateGreen : darkPrimary,
              '& + .MuiSwitch-track': {
                backgroundColor: isLight ? corporateGreen : darkPrimary,
              },
            },
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            color: isLight ? corporateGreen : darkPrimary,
            '& .MuiSlider-track': {
              background: isLight
                ? corporateGreen
                : `linear-gradient(90deg, ${darkPrimary} 0%, ${darkSecondary} 100%)`,
            },
            '& .MuiSlider-thumb': {
              backgroundColor: isLight ? corporateGreen : darkPrimary,
              '&:hover': {
                boxShadow: isLight
                  ? `0 0 0 8px ${alpha(corporateGreen, 0.16)}`
                  : `0 0 0 8px ${alpha(darkPrimary, 0.16)}`,
              },
            },
          },
        },
      },
    },
  });
};

// Default theme with system preference detection
export const theme = createAppTheme('dark');
export const lightTheme = createAppTheme('light');
export const darkTheme = createAppTheme('dark');